/**
 * RegionList.jsx — 방문 기회도 순위 목록 컴포넌트
 *
 * 지역별 방문 기회도를 순위 카드 형태로 표시합니다.
 * - 경과일·방문일수 기반 색상 + 기회도 레이블
 * - 항목 클릭 시 onItemClick 호출 (지도 연동)
 * - 선택된 지역은 인디고 테두리로 강조
 *
 * Props:
 *   regions          {object[]}   — 지역 데이터 배열
 *   loading          {boolean}    — 로딩 중 여부
 *   highlightedRegion {string|null} — 현재 강조된 지역명
 *   colorRules       {object}     — 동적 색상 기준 규칙
 *   period           {number}     — 방문일수 집계 기간 (일)
 *   onItemClick      {function}   — 항목 클릭 핸들러
 */
import React from 'react';
import { getOpportunityColor, getOpportunityLabel } from './helpers';

export default function RegionList({
  regions, loading, highlightedRegion, colorRules, period, onItemClick,
}) {
  return (
    <div style={{
      flex: 1, overflowY: 'auto',
      display: 'flex', flexDirection: 'column', gap: 5,
      overscrollBehavior: 'contain',
    }}>
      {/* 로딩 상태 */}
      {loading && (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0', fontSize: 13 }}>
          로딩 중...
        </div>
      )}

      {/* 빈 데이터 */}
      {!loading && regions.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px 0', fontSize: 13 }}>
          데이터가 없습니다.
        </div>
      )}

      {/* 지역 목록 항목 */}
      {regions.map((r, i) => {
        const daysSinceLast = r.days_since_last !== null ? Number(r.days_since_last) : null;
        const visitDays     = Number(r.visit_days_in_period);
        // 동적 색상 기준 적용
        const color         = getOpportunityColor(daysSinceLast, visitDays, colorRules);
        const label         = getOpportunityLabel(daysSinceLast, visitDays, colorRules);
        const isHighlighted = r.region === highlightedRegion;

        return (
          <div
            key={r.region}
            onClick={() => onItemClick(r)}
            style={{
              display:    'flex',
              alignItems: 'center',
              gap:        10,
              padding:    '8px 10px',
              borderRadius: 8,
              // 강조 시 인디고 테두리 + 연한 배경
              background: isHighlighted ? 'rgba(99,102,241,0.08)' : '#fff',
              border:     isHighlighted ? '1.5px solid #6366f1' : '1px solid var(--border)',
              cursor:     'pointer',
              transition: 'background 0.12s, border-color 0.12s',
              flexShrink: 0,
            }}
          >
            {/* 순위 번호 */}
            <span style={{
              width: 20, fontSize: 11, fontWeight: 800,
              color: 'var(--text-muted)', flexShrink: 0, textAlign: 'right',
            }}>
              {i + 1}
            </span>

            {/* 기회도 색상 점: 색상 링 효과 */}
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: color, flexShrink: 0,
              boxShadow: `0 0 0 2px ${color}33`,
            }} />

            {/* 지역명: 말줄임 */}
            <span style={{
              flex: 1, minWidth: 0,
              fontWeight: 700, fontSize: 13, color: 'var(--text-sub)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {r.region}
            </span>

            {/* 경과일 + 방문 횟수 + 레이블 */}
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color }}>
                {daysSinceLast === 0
                  ? '오늘 방문'
                  : daysSinceLast === null
                  ? '-'
                  : `${daysSinceLast}일 전`}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                {period}일 중 {visitDays}일 · {label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
